import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../Components/Toast';
import {
  getCadCanvasPresignedUrl,
  putCadCanvasImageToS3,
} from '../../services/cadService';
import {
  isCadCanvasImageContentType,
  readCadImageFileSize,
} from '../utils/cadCanvasImage';

/**
 * Plano base elegido pero **todavía no vinculado**. El PUT a S3 no impacta el
 * producto (quien vincula es `POST /savecadcanvasimage`), así que el archivo
 * viaja apenas se elige y Guardar solo confirma.
 *
 * Mientras el borrador vive, la UI se pinta con el `objectUrl`: el panel y el
 * mapa miran lo mismo, sin esperar a que el GET traiga `canvas_image`.
 *
 * @param {{ projectId?: string, hasRemoteImage?: boolean }} options
 * @returns {{
 *   draft: object|null,
 *   removed: boolean,
 *   dirty: boolean,
 *   uploading: boolean,
 *   pickImage: (file: File) => void,
 *   removeImage: () => void,
 *   undoRemove: () => void,
 *   reset: () => void,
 *   waitForUpload: () => Promise<{ s3Key: string, s3Url: string }|null>,
 * }}
 */
export function useCadCanvasImageDraft({ projectId, hasRemoteImage = false } = {}) {
  const [draft, setDraft] = useState(null);
  const [removed, setRemoved] = useState(false);
  const objectUrlRef = useRef(null);
  const uploadRef = useRef(null);
  // Elegir otra imagen mientras la anterior sube: el resultado viejo se tira.
  const pickTokenRef = useRef(0);

  const revokeObjectUrl = useCallback(() => {
    if (!objectUrlRef.current) return;
    URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
  }, []);

  const reset = useCallback(() => {
    pickTokenRef.current += 1;
    uploadRef.current = null;
    revokeObjectUrl();
    setDraft(null);
    setRemoved(false);
  }, [revokeObjectUrl]);

  useEffect(() => {
    reset();
  }, [projectId, reset]);

  useEffect(() => () => {
    revokeObjectUrl();
  }, [revokeObjectUrl]);

  const pickImage = useCallback((file) => {
    const id = String(projectId || '').trim();
    if (!id || !file) return;
    if (!isCadCanvasImageContentType(file.type)) {
      toast.error('Usá un PNG, JPEG o WebP.');
      return;
    }
    pickTokenRef.current += 1;
    const token = pickTokenRef.current;
    revokeObjectUrl();
    const objectUrl = URL.createObjectURL(file);
    objectUrlRef.current = objectUrl;
    setRemoved(false);
    setDraft({
      status: 'uploading',
      file,
      objectUrl,
      paintUrl: objectUrl,
      size: null,
      s3Key: '',
      s3Url: '',
    });

    const sizePromise = readCadImageFileSize(file);
    const run = (async () => {
      const presigned = await getCadCanvasPresignedUrl({
        id,
        filename: file.name,
        contentType: file.type,
      });
      if (!presigned.ok || !presigned.data?.presignedUrl) {
        throw new Error(presigned.message || 'No se obtuvo la URL de subida.');
      }
      const uploaded = await putCadCanvasImageToS3({
        presignedUrl: presigned.data.presignedUrl,
        file,
        contentType: file.type,
      });
      if (!uploaded.ok) throw new Error('No se pudo subir la imagen.');
      return { s3Key: presigned.data.s3Key, s3Url: presigned.data.s3Url };
    })();

    uploadRef.current = run;

    void sizePromise.then((size) => {
      if (pickTokenRef.current !== token || !size) return;
      setDraft((prev) => (prev ? { ...prev, size } : prev));
    });

    void (async () => {
      try {
        const result = await run;
        if (pickTokenRef.current !== token) return;
        const size = await sizePromise;
        if (pickTokenRef.current !== token) return;
        setDraft((prev) => (prev ? {
          ...prev,
          ...result,
          size: size || prev.size,
          paintUrl: prev.objectUrl,
          status: 'ready',
        } : prev));
      } catch (err) {
        if (pickTokenRef.current !== token) return;
        console.error('CAD plano base: la subida falló', err);
        uploadRef.current = null;
        setDraft((prev) => (prev ? { ...prev, status: 'error' } : prev));
        toast.error(err?.message || 'No se pudo subir la imagen.');
      }
    })();
  }, [projectId, revokeObjectUrl]);

  const removeImage = useCallback(() => {
    pickTokenRef.current += 1;
    uploadRef.current = null;
    revokeObjectUrl();
    setDraft(null);
    setRemoved(hasRemoteImage);
  }, [hasRemoteImage, revokeObjectUrl]);

  const undoRemove = useCallback(() => {
    setRemoved(false);
  }, []);

  /** Guardar puede llegar antes que el PUT: se espera, no se corta. */
  const waitForUpload = useCallback(async () => {
    const run = uploadRef.current;
    if (!run) return null;
    return run;
  }, []);

  return {
    draft,
    removed,
    dirty: Boolean(draft) || removed,
    uploading: draft?.status === 'uploading',
    pickImage,
    removeImage,
    undoRemove,
    reset,
    waitForUpload,
  };
}
